import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Download, Search, FileText, Truck } from "lucide-react";

const API_BASE = api.defaults.baseURL;

async function downloadChallanPdf(id, code) {
  try {
    const res = await fetch(`${API_BASE}/exports/challan/${id}/pdf`, { credentials: "include" });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      toast.error(`Download failed (${res.status})`, { description: txt.slice(0, 120) });
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${code.replace(/\//g, "_")}-challan.pdf`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
  } catch (e) {
    toast.error("Download failed", { description: e?.message || String(e) });
  }
}

export default function Challans() {
  const [filter, setFilter] = useState("");
  const { data } = useQuery({
    queryKey: ["challans"],
    queryFn: () => api.get("/accounts/challans").then((r) => r.data),
  });

  const items = (data?.items || []).filter((c) => {
    if (!filter) return true;
    const f = filter.toLowerCase();
    return (c.code || "").toLowerCase().includes(f) ||
      (c.customer_name || "").toLowerCase().includes(f) ||
      (c.order_code || "").toLowerCase().includes(f) ||
      (c.bowser_code || "").toLowerCase().includes(f);
  });

  const totalLitres = items.reduce((s, c) => s + (Number(c.quantity_litres) || 0), 0);

  return (
    <div className="space-y-4" data-testid="challans-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Delivery Challans</h1>
        <p className="text-sm text-muted-foreground">Auto-generated on dispatch. Financial-year numbering (DC/YY-YY/####). Download PDF for road inspection & customer proof-of-delivery.</p>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Kpi icon={FileText} label="Challans" value={items.length} />
        <Kpi icon={Truck} label="Total dispatched" value={`${totalLitres.toLocaleString("en-IN")} L`} />
        <Kpi icon={FileText} label="Filtered from" value={data?.items?.length || 0} />
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle>All challans</CardTitle>
          <div className="relative w-full max-w-64">
            <Search className="absolute left-2 top-2.5 text-muted-foreground" size={14} />
            <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search code, customer, order…" className="pl-7" data-testid="challan-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-4 py-2">Challan No.</th>
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Order</th>
                <th className="px-4 py-2">Customer</th>
                <th className="px-4 py-2">Bowser</th>
                <th className="px-4 py-2">Driver</th>
                <th className="px-4 py-2 text-right">Qty (L)</th>
                <th className="px-4 py-2 text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-border/40 text-sm" data-testid={`challan-row-${c.code}`}>
                  <td className="px-4 py-2 font-mono">{c.code}</td>
                  <td className="px-4 py-2 text-xs">{c.date?.slice(0, 10)}</td>
                  <td className="px-4 py-2">
                    <Link to={`/app/orders/${c.order_id}`} className="font-mono text-primary hover:underline" data-testid={`challan-order-link-${c.code}`}>{c.order_code}</Link>
                  </td>
                  <td className="px-4 py-2">{c.customer_name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{c.bowser_code || "—"}</td>
                  <td className="px-4 py-2 text-xs">{c.driver_name || "—"}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">{Number(c.quantity_litres).toLocaleString("en-IN")}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => downloadChallanPdf(c.id, c.code)} data-testid={`challan-dl-${c.code}`}>
                      <Download size={12} className="mr-1" /> PDF
                    </Button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  <FileText size={32} className="mx-auto mb-2 opacity-40" />
                  {filter ? "No challans match your search." : "No delivery challans yet. Challans are auto-created when an order is dispatched."}
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <Icon size={14} />
    </div>
    <div className="mt-2 font-display text-xl font-black">{value}</div>
  </div>
);
